// frontend/src/types/photoInbox.ts
/**
 * Types for the photo inbox API (docs/plans/2026-10-13-photo-inbox.md, section 9).
 * Mirrors backend/app/models/photo_inbox.py and the 409 details raised by
 * backend/app/services/photo_inbox.py. Datetimes are ISO 8601 strings.
 */

import type { TakenAtSource, TimelineEntry } from './timeline';

/**
 * Where an inbox photo goes: an organism's timeline, or the tank's own photos
 * (a tank shot). Tank shots plan, docs/plans/2026-10-28-tank-shots-implementation.md 3.2.
 */
export type AssignTarget = 'organism' | 'tank';

/** Most hashes one /check request takes. */
export const PHOTO_INBOX_CHECK_MAX = 200;

/** A photo in a tank's inbox. Dimensions are the stored master's. */
export interface PhotoInboxItem {
  id: number;
  taken_at: string;
  taken_at_source: TakenAtSource;
  width: number | null;
  height: number | null;
  created_at: string;
  /** Signed URL of the 300 px variant; null in the upload response (nothing is signed on upload). */
  thumb_url: string | null;
}

/** One page of pending photos, oldest capture date first. */
export interface PhotoInboxPage {
  items: PhotoInboxItem[];
  /** Opaque keyset cursor: pass it back as `after` for the next page. null on the last page. */
  next_after: string | null;
}

export interface PhotoInboxListParams {
  after?: string | null;
  /** 1 to 100; the server defaults to 60. */
  limit?: number;
}

export interface PhotoInboxCount {
  count: number;
}

/** One pending photo for the assign view, with its neighbours in the inbox order. */
export interface PhotoInboxDetail extends PhotoInboxItem {
  /** Signed URL of the 1920 px variant (never the master). */
  full_url: string | null;
  prev_id: number | null;
  next_id: number | null;
}

export interface PhotoInboxCheckRequest {
  /** Content hashes, 64 lowercase hex characters each, at most PHOTO_INBOX_CHECK_MAX. */
  hashes: string[];
}

export interface PhotoInboxOnLivestock {
  hash: string;
  /** The organism's name, or "Tank shot". */
  livestock_name: string;
  /** Missing from a server older than tank shots: an organism. */
  target?: AssignTarget;
}

/** A checked photo already in the tank's inbox, with its id, so the uploader can assign it. */
export interface PhotoInboxInInboxItem {
  hash: string;
  id: number;
  status: 'uploading' | 'pending' | 'assigning';
}

/** Which of the checked hashes this tank already has. */
export interface PhotoInboxCheckResponse {
  in_inbox: string[];
  on_livestock: PhotoInboxOnLivestock[];
  /** The in_inbox photos with id and status; missing from a server older than tank shots. */
  in_inbox_items?: PhotoInboxInInboxItem[];
}

/** The fields of one upload (multipart form). */
export interface PhotoInboxUpload {
  /** The original file, or the resized JPEG made from it. */
  photo: Blob;
  /** Filename sent with the photo. */
  filename: string;
  /** Identity of the ORIGINAL file (64 lowercase hex), even when `photo` is a resized copy. */
  content_hash: string;
  /** EXIF capture date read in the browser, "YYYY-MM-DDTHH:MM:SS". */
  taken_at_local?: string | null;
  /** Its UTC offset, "+HH:MM" or "-HH:MM". */
  taken_at_offset?: string | null;
  /** The original file's lastModified, in ms since 1970. */
  file_last_modified?: number | null;
}

/** A crop as fractions of the photo's width and height. */
export interface PhotoInboxCrop {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PhotoInboxAssignFields {
  /** Defaults to the photo's capture date. */
  entry_date?: string | null;
  entry_text?: string | null;
  crop?: PhotoInboxCrop | null;
}

/** To an organism's timeline. */
export interface PhotoInboxAssignToOrganism extends PhotoInboxAssignFields {
  target: 'organism';
  livestock_id: number;
}

/** As a tank shot: no livestock_id at all (the server answers 422 to one). */
export interface PhotoInboxAssignToTank extends PhotoInboxAssignFields {
  target: 'tank';
}

export type PhotoInboxAssignRequest = PhotoInboxAssignToOrganism | PhotoInboxAssignToTank;

/** An assign to an organism: its new timeline entry. */
export interface PhotoInboxOrganismResult extends TimelineEntry {
  /** Missing from a server older than tank shots. */
  target?: 'organism';
}

/**
 * An assign as a tank shot: the new tank shot (the contract's TankPhoto, kept
 * here so the inbox doesn't depend on the Tank Shots page's types).
 */
export interface PhotoInboxTankResult {
  target: 'tank';
  id: number;
  entry_text: string | null;
  entry_date: string;
  taken_at_source: TakenAtSource | null;
  photo_url: string | null;
  photo_display_url: string | null;
  photo_thumb_url: string | null;
  is_cover: boolean;
  created_at: string;
  updated_at: string;
}

/** What an assign answers: the entry or the tank shot (also when it was assigned already). */
export type PhotoInboxAssignResult = PhotoInboxOrganismResult | PhotoInboxTankResult;

export function isTankResult(result: PhotoInboxAssignResult): result is PhotoInboxTankResult {
  return result.target === 'tank';
}

export interface PhotoInboxDiscardResponse {
  id: number;
  status: 'discarded';
}

/** Lifecycle of an inbox row on the server ('failed' is an upload whose storage step failed). */
export type PhotoInboxStatus =
  | 'uploading'
  | 'pending'
  | 'assigning'
  | 'assigned'
  | 'discarded'
  | 'failed';

/** 409 codes that carry no extra fields. */
export type PhotoInboxConflictCode =
  | 'inbox_full'
  | 'cancelled'
  | 'in_progress'
  | 'not_pending'
  | 'expired'
  | 'uploaded_again'
  | 'gone';

/**
 * The `detail` of a 409 from the inbox routes. A duplicate upload is counted as
 * done by the client; `inbox_full` means "sort some photos first". A
 * duplicate's `status` is the existing row's; null when the server didn't send
 * one this client knows (only a 'pending' photo can be opened in the assign view).
 * Other 409s keep a plain string detail and are not conflicts of this type.
 */
export type PhotoInboxConflict =
  | { code: 'duplicate'; message: string; id: number; status: PhotoInboxStatus | null }
  /** A duplicate upload of a sorted photo; target is missing from a server older than tank shots. */
  | { code: 'on_livestock'; message: string; livestock_name: string; target?: AssignTarget }
  /**
   * The photo went to the other target (a tank shot when an organism was
   * asked for, or the reverse). livestock_name names the organism; null for a
   * tank shot.
   */
  | {
      code: 'assigned_elsewhere';
      message: string;
      target: AssignTarget;
      livestock_name: string | null;
    }
  | { code: PhotoInboxConflictCode; message: string };
